import React from 'react';
import classNames from 'classnames';

import { safePlay } from 'utils/mediaUtils';
import { AbstractMedia } from 'types/AbstractMedia';

type BackgroundVideoProps = {
  media: AbstractMedia;
  active: boolean;
  className: string;
  onError: () => void;
  onReadyChange: (ready: boolean) => void;
  onPosterChange: (hasPoster: boolean) => void;
};

const BackgroundVideo: React.FC<BackgroundVideoProps> = ({
  media, active, className, onError, onReadyChange, onPosterChange,
}) => {
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const [isPlayable, setIsPlayable] = React.useState(false);
  const [firstFrameImage, setFirstFrameImage] = React.useState<string>();

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (active) {
      safePlay(video);
    } else {
      video.pause();
    }
  }, [active]);

  const captureFirstFrame = React.useCallback((event: React.SyntheticEvent<HTMLVideoElement>) => {
    const video = event.currentTarget;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (context) {
      context.drawImage(video, 0, 0, canvas.width, canvas.height);
      setFirstFrameImage(canvas.toDataURL('image/png'));
    }
  }, []);

  const handleCanPlay = React.useCallback(() => {
    setIsPlayable(true);
  }, []);

  React.useEffect(() => {
    if (active) {
      onReadyChange(isPlayable);
      onPosterChange(Boolean(firstFrameImage));
    }
  }, [active, isPlayable, firstFrameImage, onReadyChange, onPosterChange]);

  return (
    <>
      {active && !isPlayable && firstFrameImage && (
        <img src={firstFrameImage} className={classNames(className, 'pointer-events-none')} alt="" />
      )}
      <video
        ref={videoRef}
        loop
        muted
        autoPlay
        playsInline
        preload="auto"
        controls={false}
        disableRemotePlayback
        disablePictureInPicture
        crossOrigin="anonymous"
        onError={onError}
        onLoadedData={captureFirstFrame}
        onCanPlayThrough={handleCanPlay}
        data-testid={active ? 'background-video' : undefined}
        className={classNames(className, 'pointer-events-none', {
          block: active && isPlayable,
          hidden: !active || !isPlayable,
        })}
      >
        <source src={media.path} />
      </video>
    </>
  );
};

export default BackgroundVideo;
